import { useEffect, useState } from "react";
import InventariosService from "../api/inventarios.api";

// Búsquedas de producto simultáneas: cada referencia es una petición al buscador
const TAMANO_LOTE = 5;

/**
 * Hook con el stock actual por almacén y la ubicación de las referencias de un
 * inventario. Los dos salen de la misma consulta al maestro de artículos.
 * Consulta por lotes y va publicando cada lote según llega, para que un
 * inventario grande no espere a la última referencia para pintar la primera.
 * @param {Array<{referencia: string}>} lineas - Líneas del inventario
 * @param {boolean} [activo=true] - false para no consultar (p. ej. inventario finalizado)
 * @returns {{ stocks: Object<string, Object<string, number>|null>,
 *   ubicaciones: Object<string, string|null>, loading: boolean }}
 *   Por referencia, sin clave mientras se consulta y `null` si la consulta falló.
 *   Si el producto no aparece: stock `{}` y ubicación `""`
 */
export const useStocks = (lineas, activo = true) => {
    const [stocks, setStocks] = useState({});
    const [ubicaciones, setUbicaciones] = useState({});
    const [loading, setLoading] = useState(false);

    // Clave estable: guardar un conteo devuelve líneas nuevas con las mismas referencias y no debe repetir la consulta
    const referencias = (lineas ?? []).map((linea) => linea.referencia).join("\n");

    useEffect(() => {
        if (!activo || !referencias) {
            setLoading(false);
            return undefined;
        }

        let vivo = true;
        const unicas = [...new Set(referencias.split("\n"))];
        setStocks({});
        setUbicaciones({});
        setLoading(true);

        (async () => {
            for (let i = 0; i < unicas.length && vivo; i += TAMANO_LOTE) {
                const lote = unicas.slice(i, i + TAMANO_LOTE);
                let stocksLote;
                let ubicacionesLote;
                try {
                    const encontrados = await InventariosService.getArticulos(lote);
                    stocksLote = Object.fromEntries(lote.map((referencia) => [referencia, encontrados[referencia]?.stockPorAlmacen ?? {}]));
                    ubicacionesLote = Object.fromEntries(lote.map((referencia) => [referencia, encontrados[referencia]?.ubicacion ?? ""]));
                } catch (err) {
                    console.error("Error al cargar el stock por almacén:", err);
                    stocksLote = Object.fromEntries(lote.map((referencia) => [referencia, null]));
                    ubicacionesLote = stocksLote;
                }
                if (vivo) {
                    setStocks((prev) => ({ ...prev, ...stocksLote }));
                    setUbicaciones((prev) => ({ ...prev, ...ubicacionesLote }));
                }
            }
            if (vivo) setLoading(false);
        })();

        return () => {
            vivo = false;
        };
    }, [referencias, activo]);

    return { stocks, ubicaciones, loading };
};
